<template>
	<div class="offer__label" v-if="promotionOffer">
		<span class="offer__label__free-trial" :class="{ 'text-grey': isHighlighted }">
			{{ freeTrialText }}
		</span>
		<span class="offer__label__price">
			<span v-if="retailPrice">{{ retailPrice }}</span>
			<!-- This will still make sure the space is still taken up (it's transparent) -->
			<span v-else-if="!localisedCountry" class="offer__label__price transparent">retail price</span>
			<img v-if="localisedCountry" class="icon-flag" :src="flagSrc" :alt="localisedCountry" />
		</span>
	</div>
</template>

<script lang="ts" setup>
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { computed } from 'vue';
import { PlanOffer } from '@/features/buybox/components/list/ListPromotionRow.vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';

const props = defineProps<{
	title: TitleDetail;
	promotionOffer: TitleOfferFragment | null;
	packagePlan: PlanOffer | null;
	isHighlighted?: boolean;
	localisedCountry?: string | null;
	isGridSwiper?: boolean;
}>();

const { jwConfig } = useRuntimeConfig().public;

const { t } = useI18n();

const retailPrice = computed(() => {
	if (props.packagePlan?.retailPrice) return t('WEBAPP_THEN_X_PER_MONTH', { price: props.packagePlan.retailPrice });
	return null;
});

const freeTrialText = computed(() => {
	// for providers which expect no packagePlan
	if (['jyn', 'jwt'].includes(props.promotionOffer?.package.shortName || '')) return t('WEBAPP_FREE');

	if (props.promotionOffer?.package.shortName === 'tbv') return t('WEBAPP_FREE_WITH_ADS');

	if (props.packagePlan?.durationDays) {
		return props.packagePlan.durationDays === 1
			? t('WEBAPP_1_DAY_FREE')
			: t('WEBAPP_X_DAYS_FREE', { days: props.packagePlan.durationDays });
	}
	return null;
});

const flagSrc = computed(() => {
	if (!props.localisedCountry) return undefined;
	const countryCode = props.localisedCountry === 'GB' ? 'UK' : props.localisedCountry;
	return `${jwConfig.ASSETS_URL}/flags/${countryCode}.png`;
});
</script>

<style lang="scss" scoped>
.offer__label {
	@media (max-width: 1024px) {
		text-align: end;
		justify-self: flex-end;
		max-width: calc(50vw - 32px);
	}

	&__free-trial {
		margin: 0;
		color: var(--jw-yellow-200);
		font-size: 14px;
		font-weight: 900;
		line-height: 16px;
		letter-spacing: 0.21px;
		margin-bottom: 4px;

		&.cta-gray {
			color: var(--jw-grey-200);
		}
	}

	&__price {
		margin: 0;
		color: var(--jw-grey-200);
		font-size: 14px;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.21px;
		display: -webkit-box;
		line-clamp: 2;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}

.transparent {
	color: transparent;
}

.icon-flag {
	position: relative;
	width: 20px;
	left: 3px;
	top: -1px;
}
</style>
